import { TRAINING_LEVEL_LABELS, TRAINING_LEVEL_LABELS_EN, type TrainingLevel } from "../data/securityModules";
import { authFetch } from "./authFetch";
import type { Lang } from "./customModule";

// Tenant-eigene Trainings-Pläne: ersetzen die 4 fest codierten Level
// (basis/standard/erweitert/compliance) durch eine umbenennbare, bis zu
// 6 Einträge lange Liste. Reihenfolge im Array = kumulativer Rang (Index 0
// sieht am wenigsten, der letzte Eintrag sieht alles darunter plus sich selbst).
// Persistenz: tenant_entitlements.features.training_plans (JSONB, kein Schema-Change).
// Pläne haben bewusst keine eigene Farbe (mehr): überall neutral dargestellt,
// aktive Auswahl nur in der Markenfarbe. Alte gespeicherte `color`-Felder werden ignoriert.
export type TrainingPlan = {
  id: string;
  name: string;
};

/** Einheitliche Akzentfarbe für ausgewählte Pläne (Buttons, Badges). */
export const TRAINING_PLAN_ACCENT = "#1B6AB1";

export const MAX_TRAINING_PLANS = 6;

export function defaultTrainingPlans(lang: Lang): TrainingPlan[] {
  const labels = lang === "en" ? TRAINING_LEVEL_LABELS_EN : TRAINING_LEVEL_LABELS;
  return (["basis", "standard", "erweitert", "compliance"] as TrainingLevel[]).map(id => ({
    id,
    name: labels[id],
  }));
}

export function normalizeTrainingPlans(raw: unknown): TrainingPlan[] {
  if (!Array.isArray(raw)) return [];
  const seenIds = new Set<string>();
  const plans: TrainingPlan[] = [];
  raw.slice(0, MAX_TRAINING_PLANS).forEach(entry => {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) return;
    const candidate = entry as Record<string, unknown>;
    const id = typeof candidate.id === "string" ? candidate.id.trim().slice(0, 60) : "";
    const name = typeof candidate.name === "string" ? candidate.name.trim().slice(0, 80) : "";
    if (!id || !name || seenIds.has(id)) return;
    seenIds.add(id);
    plans.push({ id, name });
  });
  return plans;
}

/** Liefert die aktiven Pläne eines Tenants, oder die 4 Standard-Pläne, falls nichts konfiguriert ist. */
export function readTrainingPlans(features: { training_plans?: unknown } | null | undefined, lang: Lang): TrainingPlan[] {
  const stored = features && typeof features === "object" ? normalizeTrainingPlans(features.training_plans) : [];
  return stored.length > 0 ? stored : defaultTrainingPlans(lang);
}

/** Kumulativer Rang eines Modul-Plans (Index in der Tenant-Planliste). Unbekannte IDs liegen über allen Plänen: das Modul bekommt niemand. */
export function trainingPlanRank(planId: string, plans: TrainingPlan[]): number {
  const idx = plans.findIndex(p => p.id === planId);
  return idx === -1 ? plans.length : idx;
}

/**
 * Rang eines Mitarbeiters. Eine veraltete Plan-ID (Plan gelöscht) fällt auf den
 * untersten Plan – früher galt sie als höchster Rang und schaltete alles frei.
 * Gleiche Regel wie userPlanRank() in api/_modulePlan.js.
 */
export function userTrainingPlanRank(planId: string, plans: TrainingPlan[]): number {
  const idx = plans.findIndex(p => p.id === planId);
  return idx === -1 ? 0 : idx;
}

/** Anzeigename eines Plans in der aktuellen Tenant-Konfiguration, mit Fallback auf den Default-Namen. */
export function trainingPlanLabel(planId: string, plans: TrainingPlan[], lang: Lang): string {
  const plan = plans.find(p => p.id === planId);
  if (plan) return plan.name;
  const labels = lang === "en" ? TRAINING_LEVEL_LABELS_EN : TRAINING_LEVEL_LABELS;
  return labels[planId] ?? planId;
}

/** `defaultLevel` mitgeben, wenn der Default-Plan für neue Mitarbeiter mit umziehen muss (z.B. weil er gelöscht wurde). */
export async function saveTrainingPlansForTenant(tenantId: string, plans: TrainingPlan[], defaultLevel?: string): Promise<boolean> {
  if (!tenantId) return false;
  try {
    const res = await authFetch("/api/entitlements", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tenantId, trainingPlansPatch: { plans: normalizeTrainingPlans(plans), ...(defaultLevel ? { defaultLevel } : {}) } }),
    });
    return res.ok;
  } catch {
    return false;
  }
}
